import { h, resolveComponent } from 'vue';
import {
    createRouter,
    createWebHashHistory,
} from 'vue-router';

import { authService } from '@services/authService';
import {
    CART_TYPE_REGULAR,
    CART_TYPE_STORE,
    CART_TYPE_CLAIM,
    FORM_TYPE_ADD,
    FORM_TYPE_EDIT,
    FORM_TYPE_DISPLAY,
    STATEMENT_TYPE_AR,
    STATEMENT_TYPE_SALES_TAX,
    STATEMENT_TYPE_CUSTOMER_POS,
    PAGE_SITE_ADMIN_MANAGE_USERS,
    PAGE_SUPER_ADMIN_MANAGE_USERS
} from '@/constants/Constants';

// Containers
const TheContainer = () => import('@/containers/TheContainer');

// Views - Pages
const Page404 = () => import('@/views/pages/Page404');
const Page500 = () => import('@/views/pages/Page500');
const NotAuth = () => import('@/views/pages/NotAuth');
const Login = () => import('@/views/pages/Login');
const ForgotPassword = () => import('@/views/pages/ForgotPassword');
const ResetPassword = () => import('@/views/pages/ResetPassword');
const EmailPrompt = () => import('@/views/pages/EmailPrompt');
const LegacyPasswordPrompt = () => import('@/views/pages/LegacyPasswordPrompt');

// Orders (Catalog)
const OrdersRegularCatalog = () => import('@/views/orders/OrderRegularCatalog');

// Manage Users Page
const ManageUsers = () => import('@/views/admin/ManageUsers');

// User Form Page
const UserForm = () => import('@/views/admin/UserForm');

// Change Password Page
const ChangePassword = () => import('@/views/account/ChangePassword');

// Change Narcotic Control Code Page
const ChangeNarcoticCode = () => import('@/views/account/ChangeNarcoticCode');

// Manage Store Settings Page
const ManageStoreSettings = () => import('@/views/admin/ManageStoreSettings');

// Cart Page
const CartPage = () => import('@/views/carts/CartPage.vue');

// Cart Order Confirmation
const CartOrderConfirmation = () => import('@/views/carts/CartOrderConfirmation');

// Select Items for Labels page
const SelectItemLabels = () => import('@/views/labels/SelectItemLabels');

// Print Labels page
const PrintLabelQueue = () => import('@/views/labels/PrintLabels');

// Narcotic Control Form Inquiry
const NcfInquiry = () => import('@/views/inquiries/NcfInquiry');

// Narcotic Control Form Details Page
const NcfDetails = () => import('@/views/inquiries/NcfDetails');

// Print Narcotic Control Forms Page
const PrintNarcoticForms = () => import('@/views/printViews/PrintNarcoticForms');

// Invoice Inquiry
const InvoiceInquiry = () => import('@/views/inquiries/InvoiceInquiry');

// Invoice Details Page
const InvoiceDetails = () => import('@/views/inquiries/InvoiceDetails');

// Print Invoices Page
const PrintInvoices = () => import('@/views/printViews/PrintInvoices');

// Order Inquiry
const OrderInquiry = () => import('@/views/inquiries/OrderHistoryInquiry');

// Order Details Page
const OrderDetails = () => import('@/views/inquiries/OrderHistoryDetails');

// Print Orders Page
const PrintOrders = () => import('@/views/printViews/PrintOrders');

// Item Search Inquiry Page
const ItemSearchInquiry = () => import('@/views/inquiries/ItemSearchInquiry');

// Saved Items List Page
const SavedItemsList = () => import('@/views/orders/SavedItemsList');

// Saved Items List Details Page
const SavedItemsListDetails = () => import('@/views/orders/SavedItemsListDetails');

// SRP Classes Page
const SrpClasses = () => import('@/views/maintenance/SrpClasses');

// Maintain Item SRPs Page
const MaintainItemSrps = () => import('@/views/maintenance/MaintainItemSrps');

// Changed Items Inquiry
const ChangedItemsInquiry = () => import('@/views/inquiries/ChangedItemsInquiry');

// Top Sellers Inquiry
const TopSellersInquiry = () => import('@/views/inquiries/TopSellersInquiry');

// My Top Ordered Items Inquiry
const MyTopItemsInquiry = () => import('@/views/inquiries/MyTopItemsInquiry');

// Statement Inquiry
const StatementInquiry = () => import('@/views/inquiries/StatementInquiry');

// Statement Details
const StatementDetails = () => import('@/views/inquiries/StatementDetails');

// Print Statements
const PrintStatements = () => import('@/views/printViews/PrintStatements');

// New Items Catalog
const NewItemsCatalog = () => import('@/views/orders/NewItemsCatalog');

// Quick Order
const QuickOrder = () => import('@/views/orders/QuickOrder');

// Upload Order
const UploadOrder = () => import('@/views/orders/UploadOrder');

// Unipharm Admin Manage Users Page
const UnipharmAdminManageUsers = () => import('@/views/unipharmAdmin/UnipharmAdminManageUsers');

// Unipharm Admin - Manage Resources Page
const UnipharmAdminManageResources = () => import('@/views/unipharmAdmin/ManageResources');

// Unipharm Admin - Resource Form Page
const UnipharmAdminResourceForm = () => import('@/views/unipharmAdmin/ResourceForm');

// Unipharm Admin - Manage Resource Types Page
const UnipharmAdminManageResourceTypes = () => import('@/views/unipharmAdmin/ManageResourceTypes');

// Unipharm Admin - Resource Type Form Page
const UnipharmAdminResourceTypeForm = () => import('@/views/unipharmAdmin/ResourceTypeForm');

// Resources
const Resources = () => import('@/views/resources/HelpAndResources');

// Monthly Purchases Report
const MonthlyPurchasesReport = () => import('@/views/reports/MonthlyPurchases');

// Monthly Purchases Report - Account Group
const MonthlyPurchasesAccountGroupReport = () => import('@/views/reports/MonthlyPurchasesAccountGroup');

// Monthly Purchases Report - Item Group
const MonthlyPurchasesItemGroupReport = () => import('@/views/reports/MonthlyPurchasesItemGroup');

// Cart Adjustments Confirmation
const CartAdjustmentsConfirmation = () => import('@/views/carts/CartAdjustmentsConfirmation');

// Item Details
const ItemDetails = () => import('@/views/orders/ItemDetails');

// Order Confirmation Details
const OrderConfirmationDetails = () => import('@/views/inquiries/OrderConfirmationDetails');

const router = createRouter({
    history: createWebHashHistory(),
    linkActiveClass: 'active',
    scrollBehavior: () => ({ top: 0 }),
    routes: configRoutes()
});

export default router;

function configRoutes() {
    return [
        {
            path: '',
            redirect: '/orders/regular',
            name: 'Home',
            component: TheContainer,
            beforeEnter: authService.requireAuth,
            children: [
                {
                    path: '/orders',
                    redirect: '/orders/regular',
                    name: 'Orders',
                    meta: {
                        label: 'Order',
                    },
                    component: {
                        render() {
                            return h(resolveComponent('router-view'));
                        }
                    },
                    beforeEnter: authService.requireAuth,
                    children: [ 
                        {
                            name: 'Regular Catalog',
                            path: '/orders/regular',
                            meta: {
                                label: 'Regular Catalog',
                                resource: 'RegularCatalog',
                            },
                            beforeEnter: authService.requireAuth,
                            component: OrdersRegularCatalog
                        },
                        {
                            name: 'Item Details',
                            path: '/orders/itemDetails/:itemNumber/:displayTab?',
                            meta: {
                                label: 'Item Details',
                            },
                            props: (route) => ({
                                itemNumber: route.params.itemNumber,
                                displayTab: route.params.displayTab,
                            }),
                            component: ItemDetails
                        },
                        {
                            name: 'New Items Catalog',
                            path: '/orders/newItems',
                            meta: {
                                label: 'New Items Catalog',
                                // TODO - Ask Unipharm if we should add a specific authority for this page
                                // resource: 'Order',
                            },
                            beforeEnter: authService.requireAuth,
                            component: NewItemsCatalog
                        },
                        {
                            name: 'Saved Items Lists',
                            path: '/orders/savedItemsLists',
                            meta: {
                                label: 'Saved Items Lists',
                                resource: 'SavedItemsList',
                            },
                            beforeEnter: authService.requireAuth,
                            component: SavedItemsList
                        },
                        {
                            name: 'Add Saved Items List',
                            path: '/orders/addSavedItemsList/:cartType?',
                            meta: {
                                label: 'Add Saved Items List',
                                resource: 'SavedItemsList',
                            },
                            props: (route) => ({
                                formType: FORM_TYPE_ADD,
                                createFromCart: route.params.cartType,
                            }),
                            beforeEnter: authService.requireAuth,
                            component: SavedItemsListDetails
                        },
                        {
                            name: 'Edit Saved Items List',
                            path: '/orders/editSavedItemsList/:id',
                            meta: {
                                label: 'Edit Saved Items List',
                                resource: 'SavedItemsList',
                            },
                            props: (route) => ({
                                formType: FORM_TYPE_EDIT,
                                listId: route.params.id,
                            }),
                            beforeEnter: authService.requireAuth,
                            component: SavedItemsListDetails
                        },
                        {
                            name: 'Display Saved Items List',
                            path: '/orders/displaySavedItemsList/:id',
                            meta: {
                                label: 'Display Saved Items List',
                                resource: 'SavedItemsList',
                            },
                            props: (route) => ({
                                formType: FORM_TYPE_DISPLAY,
                                listId: route.params.id,
                            }),
                            beforeEnter: authService.requireAuth,
                            component: SavedItemsListDetails
                        },
                        {
                            name: 'Quick Order',
                            path: '/orders/quickOrder',
                            meta: {
                                label: 'Quick Order',
                                resource: 'QuickOrder',
                            },
                            beforeEnter: authService.requireAuth,
                            component: QuickOrder
                        },
                        {
                            name: 'Upload Order File',
                            path: '/orders/uploadOrder',
                            meta: {
                                label: 'Upload Order',
                                resource: ['RegularCart', 'SubmitClaims', 'PrintLabels'],
                            },
                            beforeEnter: authService.requireAuthAtLeastOne,
                            component: UploadOrder
                        },
                    ]
                },
                {
                    path: '/admin',
                    redirect: '/admin/manageStoreSettings',
                    name: 'Site Admin',
                    meta: {
                        label: 'Admin',
                    },
                    beforeEnter: authService.requireAuth,
                    component: {
                        render() {
                            return h(resolveComponent('router-view'));
                        }
                    },
                    children: [ 
                        {
                            path: '/admin/manageUsers',
                            name: 'Manage Users',
                            meta: {
                                label: 'Manage Users',
                                resource: 'SiteManagement',
                            },
                            beforeEnter: authService.requireAuth,
                            component: ManageUsers,
                        },
                        {
                            path: '/admin/manageUsers/addUser',
                            name: 'Add User',
                            meta: {
                                label: 'Add User',
                                resource: 'SiteManagement',
                            },
                            props: {
                                callingPage: PAGE_SITE_ADMIN_MANAGE_USERS,
                                formType: FORM_TYPE_ADD,
                                userId: null
                            },
                            beforeEnter: authService.requireAuth,
                            component: UserForm,
                        },
                        {
                            path: '/admin/manageUsers/editUser/:id',
                            name: 'Edit User',
                            meta: {
                                label: 'Edit User',
                                resource: 'SiteManagement',
                            },
                            props: (route) => ({
                                callingPage: PAGE_SITE_ADMIN_MANAGE_USERS,
                                formType: FORM_TYPE_EDIT,
                                userId: route.params.id
                            }),
                            beforeEnter: authService.requireAuth,
                            component: UserForm
                        },
                        {
                            path: '/admin/manageUsers/displayUser/:id',
                            name: 'Display User',
                            meta: {
                                label: 'Display User',
                                resource: 'SiteManagement',
                            },
                            props: (route) => ({
                                callingPage: PAGE_SITE_ADMIN_MANAGE_USERS,
                                formType: FORM_TYPE_DISPLAY,
                                userId: route.params.id
                            }),
                            beforeEnter: authService.requireAuth,
                            component: UserForm
                        },
                        {
                            path: '/admin/manageStoreSettings',
                            name: 'Manage Store Settings',
                            meta: {
                                label: 'Manage Store Settings',
                                resource: 'SiteManagement',
                            },
                            beforeEnter: authService.requireAuth,
                            component: ManageStoreSettings
                        },
                        {
                            path: '/admin/changePassword',
                            name: 'Change Password',
                            meta: {
                                label: 'Change Password',
                                resource: 'ChangePassword',
                            },
                            beforeEnter: authService.requireAuth,
                            component: ChangePassword
                        },
                        {
                            path: '/admin/resetNarcoticCode',
                            name: 'Reset Narcotic Control Code',
                            meta: {
                                label: 'Reset Narcotic Control Code',
                                resource: 'IsPharmacist',
                            },
                            beforeEnter: authService.requireAuth,
                            component: ChangeNarcoticCode
                        },
                    ]
                },
                {
                    path: '/carts',
                    redirect: '/carts/regularCart',
                    name: 'Carts',
                    meta: {
                        label: 'Carts',
                    },
                    beforeEnter: authService.requireAuth,
                    component: {
                        render() {
                            return h(resolveComponent('router-view'));
                        }
                    },
                    children: [ 
                        {
                            path: '/carts/regularCart',
                            name: 'Regular Cart',
                            meta: {
                                label: 'Regular Cart',
                                resource: 'RegularCart',
                            },
                            props: {
                                cartType: CART_TYPE_REGULAR,
                            },
                            beforeEnter: authService.requireAuth,
                            component: CartPage
                        },
                        {
                            path: '/carts/storeCart',
                            name: 'Store Cart',
                            meta: {
                                label: 'Store Cart',
                                resource: 'StoreCart',
                            },
                            props: {
                                cartType: CART_TYPE_STORE,
                            },
                            beforeEnter: authService.requireAuth,
                            component: CartPage
                        },
                        {
                            path: '/carts/cartConfirmation/:cart',
                            name: 'Cart Confirmation',
                            meta: {
                                label: 'Cart Confirmation',
                                resource: ['PlaceOrders', 'RegularCart', 'StoreCart', 'SubmitClaims'],
                            },
                            props: (route) => ({
                                confirmationType: 'Cart',
                                cartToDisplay: route.params.cart
                            }),
                            beforeEnter: authService.requireAuthAtLeastOne,
                            component: CartOrderConfirmation
                        },
                        {
                            path: '/carts/orderConfirmation/:cart',
                            name: 'Order Confirmation',
                            meta: {
                                label: 'Order Confirmation',
                                resource: 'PlaceOrders',
                            },
                            props: (route) => ({
                                confirmationType: 'Order',
                                cartToDisplay: route.params.cart
                            }),
                            beforeEnter: authService.requireAuth,
                            component: CartOrderConfirmation
                        },
                        // TODO: To be implemented post go live
                        /*
                        {
                            path: 'promoCart',
                            name: 'Promo Cart',
                            meta: {
                                label: 'Promo Cart',
                                resource: 'PromoCart',
                            },
                            beforeEnter: authService.requireAuth,
                            component: RegularCart // TODO - Update component once Promo Cart has been implemented
                        },
                        */
                        
                        {
                            path: '/carts/claimCart',
                            name: 'Claim Cart',
                            meta: {
                                label: 'Claim Cart',
                                resource: 'SubmitClaims',
                            },
                            props: {
                                cartType: CART_TYPE_CLAIM,
                            },
                            beforeEnter: authService.requireAuth,
                            component: CartPage
                        },
                        {
                            path: '/carts/regularCart/adjustmentsConfirmation',
                            name: 'Regular Cart Adjustments Confirmation',
                            meta: {
                                label: 'Cart Adjustments Confirmation',
                                resource: 'PlaceOrders',
                            },
                            props: {
                                cartType: CART_TYPE_REGULAR
                            },
                            beforeEnter: authService.requireAuth,
                            component: CartAdjustmentsConfirmation
                        },
                        {
                            path: '/carts/storeCart/adjustmentsConfirmation',
                            name: 'Store Cart Adjustments Confirmation',
                            meta: {
                                label: 'Cart Adjustments Confirmation',
                                resource: 'PlaceOrders',
                            },
                            props: {
                                cartType: CART_TYPE_STORE
                            },
                            beforeEnter: authService.requireAuth,
                            component: CartAdjustmentsConfirmation
                        },
                    ]
                },
                {
                    path: '/labels',
                    redirect: '/labels/selectItemLabels',
                    name: 'Labels',
                    meta: {
                        label: 'Labels',
                    },
                    beforeEnter: authService.requireAuth,
                    component: {
                        render() {
                            return h(resolveComponent('router-view'));
                        }
                    },
                    children: [ 
                        {
                            path: '/labels/selectItemLabels',
                            name: 'Select Item Labels',
                            meta: {
                                label: 'Select Item Labels',
                                resource: 'PrintLabels',
                            },
                            beforeEnter: authService.requireAuth,
                            component: SelectItemLabels
                        },
                        {
                            path: '/labels/printLabelQueue',
                            name: 'Print Label Queue',
                            meta: {
                                label: 'Print Label Queue',
                                resource: 'PrintLabels',
                            },
                            beforeEnter: authService.requireAuth,
                            component: PrintLabelQueue
                        },
                    ]
                },
                {
                    path: '/inquiries',
                    redirect: '/inquiries/orderInquiry',
                    name: 'Inquiries',
                    meta: {
                        label: 'Inquiries',
                    },
                    beforeEnter: authService.requireAuth,
                    component: {
                        render() {
                            return h(resolveComponent('router-view'));
                        }
                    },
                    children: [ 
                        {
                            name: 'Narcotic Control Form Inquiry',
                            path: '/inquiries/ncfInquiry',
                            meta: {
                                label: 'Narcotic Control Form Inquiry',
                                resource: 'NarcoticControlForms',
                            },
                            beforeEnter: authService.requireAuth,
                            component: NcfInquiry
                        },
                        {
                            name: 'Narcotic Control Form Details',
                            path: '/inquiries/ncfDetails/:formId',
                            meta: {
                                label: 'Narcotic Control Form Details',
                                resource: 'NarcoticControlForms',
                            },
                            props: (route) => ({
                                formId: route.params.formId
                            }),
                            beforeEnter: authService.requireAuth,
                            component: NcfDetails
                        },
                        {
                            name: 'Print Narcotic Control Forms',
                            path: '/inquiries/printNcfs/:formIds',
                            meta: {
                                label: 'Print Narcotic Control Forms',
                                resource: 'NarcoticControlForms',
                            },
                            props: (route) => ({
                                formIds: route.params.formIds
                            }),
                            beforeEnter: authService.requireAuth,
                            component: PrintNarcoticForms
                        },
                        {
                            name: 'Invoice Inquiry',
                            path: '/inquiries/invoiceInquiry',
                            meta: {
                                label: 'Invoice Inquiry',
                                resource: 'InvoiceInquiry',
                            },
                            props: {
                                searchType: 'Inquiry'
                            },
                            beforeEnter: authService.requireAuth,
                            component: InvoiceInquiry
                        },
                        {
                            name: 'Invoice Details',
                            path: '/inquiries/invoiceDetails/:invoiceNum',
                            meta: {
                                label: 'Invoice Details',
                                resource: 'InvoiceInquiry',
                            },
                            props: (route) => ({
                                invoiceNum: route.params.invoiceNum
                            }),
                            beforeEnter: authService.requireAuth,
                            component: InvoiceDetails
                        },
                        {
                            name: 'Print Invoices',
                            path: '/inquiries/printInvoices/:invoiceIds',
                            meta: {
                                label: 'Print Invoices',
                                resource: 'InvoiceInquiry',
                            },
                            props: (route) => ({
                                invoiceIds: route.params.invoiceIds
                            }),
                            beforeEnter: authService.requireAuth,
                            component: PrintInvoices
                        },
                        {
                            name: 'Order History Inquiry',
                            path: '/inquiries/orderInquiry',
                            meta: {
                                label: 'Order Inquiries',
                                resource: 'OrderInquiry',
                            },
                            beforeEnter: authService.requireAuth,
                            component: OrderInquiry
                        },
                        {
                            name: 'Order Details',
                            path: '/inquiries/orderDetails/:orderNum',
                            meta: {
                                label: 'Order Details',
                                resource: 'OrderInquiry',
                            },
                            props: (route) => ({
                                orderNum: route.params.orderNum
                            }),
                            beforeEnter: authService.requireAuth,
                            component: OrderDetails
                        },
                        {
                            name: 'Order Confirmation Details',
                            path: '/inquiries/orderConfirmationDetails/:orderNum',
                            meta: {
                                label: 'Order Confirmation Details',
                                resource: 'OrderInquiry',
                            },
                            props: (route) => ({
                                orderNum: route.params.orderNum
                            }),
                            beforeEnter: authService.requireAuth,
                            component: OrderConfirmationDetails
                        },
                        {
                            name: 'Print Orders',
                            path: '/inquiries/printOrders/:orderIds',
                            meta: {
                                label: 'Print Order Inquiries',
                                resource: 'OrderInquiry',
                            },
                            props: (route) => ({
                                orderIds: route.params.orderIds
                            }),
                            beforeEnter: authService.requireAuth,
                            component: PrintOrders
                        },
                        {
                            name: 'Item Search Inquiry',
                            path: '/inquiries/itemSearchInquiry',
                            meta: {
                                label: 'Item Search Inquiry',
                                // TODO - Ask Unipharm if we should add a specific authority for this page
                                // resource: 'Inquiries',
                            },
                            beforeEnter: authService.requireAuth,
                            component: ItemSearchInquiry
                        },
                        {
                            name: 'Changed Items Inquiry',
                            path: '/inquiries/changedItemsInquiry',
                            meta: {
                                label: 'Changed Items Inquiry',
                                resource: 'ChangedItems',
                            },
                            beforeEnter: authService.requireAuth,
                            component: ChangedItemsInquiry
                        },
                        // TODO: To be implemented post go live
                        /*
                        {
                            name: 'Top Sellers Inquiry',
                            path: '/inquiries/topSellers',
                            meta: {
                                label: 'Top Sellers Inquiry',
                                resource: 'TopSellersInquiry',
                            },
                            beforeEnter: authService.requireAuth,
                            component: TopSellersInquiry
                        },
                        {
                            name: 'My Top Ordered Items Inquiry',
                            path: '/inquiries/myTopItems',
                            meta: {
                                label: 'My Top Ordered Items Inquiry',
                                resource: 'MyTopOrderedInquiry',
                            },
                            beforeEnter: authService.requireAuth,
                            component: MyTopItemsInquiry
                        },
                        */
                        {
                            name: 'Statement Inquiry',
                            path: '/inquiries/statements',
                            meta: {
                                label: 'Statement Inquiry',
                                resource: 'StatementInquiry',
                            },
                            beforeEnter: authService.requireAuth,
                            component: StatementInquiry
                        },
                        {
                            name: 'Print Statements',
                            path: '/inquiries/printStatements/:statementIds/:statementTypes',
                            meta: {
                                label: 'Print Statements',
                                resource: 'StatementInquiry',
                            },
                            props: (route) => ({
                                statementIds: route.params.statementIds,
                                statementTypes: route.params.statementTypes
                            }),
                            beforeEnter: authService.requireAuth,
                            component: PrintStatements
                        },
                        {
                            name: 'AR Statement Details',
                            path: '/inquiries/statementDetails/ar/:statementId',
                            meta: {
                                label: 'AR Statement Details',
                                resource: 'StatementInquiry',
                            },
                            props: (route) => ({
                                statementType: STATEMENT_TYPE_AR,
                                statementId: route.params.statementId
                            }),
                            beforeEnter: authService.requireAuth,
                            component: StatementDetails
                        },
                        {
                            name: 'Customer POS Summary Statement Details',
                            path: '/inquiries/statementDetails/pos/:statementId',
                            meta: {
                                label: 'Customer POS Summary Statement Details',
                                resource: 'StatementInquiry',
                            },
                            props: (route) => ({
                                statementType: STATEMENT_TYPE_CUSTOMER_POS,
                                statementId: route.params.statementId
                            }),
                            beforeEnter: authService.requireAuth,
                            component: StatementDetails
                        },
                        {
                            name: 'Sales Tax Collected Statement Details',
                            path: '/inquiries/statementDetails/tax/:statementId',
                            meta: {
                                label: 'Sales Tax Collected Statement Details',
                                resource: 'StatementInquiry',
                            },
                            props: (route) => ({
                                statementType: STATEMENT_TYPE_SALES_TAX,
                                statementId: route.params.statementId
                            }),
                            beforeEnter: authService.requireAuth,
                            component: StatementDetails
                        },
                    ]
                },
                {
                    path: '/maintenance',
                    redirect: '/maintenance/srpClasses',
                    name: 'Maintenance',
                    meta: {
                        label: 'Maintenance',
                    },
                    beforeEnter: authService.requireAuth,
                    component: {
                        render() {
                            return h(resolveComponent('router-view'));
                        }
                    },
                    children: [ 
                        {
                            path: '/maintenance/srpClasses',
                            name: 'SRP Classes',
                            meta: {
                                label: 'Class SRPs',
                                resource: 'MaintainSrps',
                            },
                            beforeEnter: authService.requireAuth,
                            component: SrpClasses
                        },
                        {
                            path: '/maintenance/maintainClassSrps/:classCode/:itemNumber?',
                            name: 'Maintain Class SRPs',
                            meta: {
                                label: 'Maintain Class SRPs',
                                resource: 'MaintainSrps',
                            },
                            props: (route) => ({
                                pageType: 'Class',
                                classCode: route.params.classCode,
                                filterItemNumber: route.params.itemNumber,
                            }),
                            beforeEnter: authService.requireAuth,
                            component: MaintainItemSrps
                        },
                        {
                            path: '/maintenance/invoiceSrps',
                            name: 'Invoice SRPs',
                            meta: {
                                label: 'Invoice SRPs',
                                resource: 'MaintainSrps',
                            },
                            props: {
                                searchType: 'SRPs'
                            },
                            beforeEnter: authService.requireAuth,
                            component: InvoiceInquiry
                        },
                        {
                            path: '/maintenance/maintainInvoiceSrps/:invoiceNum',
                            name: 'Maintain Invoice SRPs',
                            meta: {
                                label: 'Maintain Invoice SRPs',
                                resource: 'MaintainSrps',
                            },
                            props: (route) => ({
                                pageType: 'Invoice',
                                invoiceNumber: route.params.invoiceNum,
                            }),
                            beforeEnter: authService.requireAuth,
                            component: MaintainItemSrps
                        },
                    ]
                },
                {
                    path: '/unipharmAdmin',
                    redirect: '/unipharmAdmin/manageUsers',
                    name: 'Unipharm Admin',
                    meta: {
                        label: 'Unipharm Admin',
                        resource: ['SuperAdmin', 'UnipharmAdmin'],
                    },
                    beforeEnter: authService.requireAuth,
                    component: {
                        render() {
                            return h(resolveComponent('router-view'));
                        }
                    },
                    children: [ 
                        {
                            path: '/unipharmAdmin/manageUsers',
                            name: 'Unipharm Admin Manage Users',
                            meta: {
                                label: 'Unipharm Admin Manage Users',
                                resource: ['SuperAdmin', 'UnipharmAdmin'],
                            },
                            beforeEnter: authService.requireAuth,
                            component: UnipharmAdminManageUsers,
                        },
                        {
                            path: '/unipharmAdmin/manageUsers/addUser',
                            name: 'Unipharm Admin Add User',
                            meta: {
                                label: 'Unipharm Admin Add User',
                                resource: ['SuperAdmin', 'UnipharmAdmin'],
                            },
                            props: {
                                callingPage: PAGE_SUPER_ADMIN_MANAGE_USERS,
                                formType: FORM_TYPE_ADD,
                                userId: null,
                                storeId: null
                            },
                            beforeEnter: authService.requireAuth,
                            component: UserForm,
                        },
                        {
                            path: '/unipharmAdmin/manageUsers/editUser/:storeId/:id',
                            name: 'Unipharm Admin Edit User',
                            meta: {
                                label: 'Unipharm Admin Edit User',
                                resource: ['SuperAdmin', 'UnipharmAdmin'],
                            },
                            props: (route) => ({
                                callingPage: PAGE_SUPER_ADMIN_MANAGE_USERS,
                                formType: FORM_TYPE_EDIT,
                                userId: route.params.id,
                                storeId: route.params.storeId
                            }),
                            beforeEnter: authService.requireAuth,
                            component: UserForm
                        },
                        {
                            path: '/unipharmAdmin/manageUsers/displayUser/:storeId/:id',
                            name: 'Unipharm Admin Display User',
                            meta: {
                                label: 'Unipharm Admin Display User',
                                resource: ['SuperAdmin', 'UnipharmAdmin'],
                            },
                            props: (route) => ({
                                callingPage: PAGE_SUPER_ADMIN_MANAGE_USERS,
                                formType: FORM_TYPE_DISPLAY,
                                userId: route.params.id,
                                storeId: route.params.storeId
                            }),
                            beforeEnter: authService.requireAuth,
                            component: UserForm
                        },
                        {
                            path: '/unipharmAdmin/manageResources',
                            name: 'Manage Resources',
                            meta: {
                                label: 'Manage Resources',
                                resource: 'ResourceAdmin',
                            },
                            beforeEnter: authService.requireAuth,
                            component: UnipharmAdminManageResources,
                        },
                        {
                            path: '/unipharmAdmin/manageResources/addResource',
                            name: 'Add Resource',
                            meta: {
                                label: 'Add Resource',
                                resource: 'ResourceAdmin',
                            },
                            props: {
                                formType: FORM_TYPE_ADD,
                                resourceId: null
                            },
                            beforeEnter: authService.requireAuth,
                            component: UnipharmAdminResourceForm,
                        },
                        {
                            path: '/unipharmAdmin/manageResources/editResource/:id',
                            name: 'Edit Resource',
                            meta: {
                                label: 'Edit Resource',
                                resource: 'ResourceAdmin',
                            },
                            props: (route) => ({
                                formType: FORM_TYPE_EDIT,
                                resourceId: route.params.id
                            }),
                            beforeEnter: authService.requireAuth,
                            component: UnipharmAdminResourceForm
                        },
                        {
                            path: '/unipharmAdmin/manageResourceTypes',
                            name: 'Manage Resource Types',
                            meta: {
                                label: 'Manage Resource Types',
                                resource: 'ResourceAdmin',
                            },
                            beforeEnter: authService.requireAuth,
                            component: UnipharmAdminManageResourceTypes,
                        },
                        {
                            path: '/unipharmAdmin/manageResourceTypes/addResourceType',
                            name: 'Add Resource Type',
                            meta: {
                                label: 'Add Resource Type',
                                resource: 'ResourceAdmin',
                            },
                            props: {
                                formType: FORM_TYPE_ADD,
                                resourceTypeId: null,
                            },
                            beforeEnter: authService.requireAuth,
                            component: UnipharmAdminResourceTypeForm,
                        },
                        {
                            path: '/unipharmAdmin/manageResourceTypes/editResourceType/:id',
                            name: 'Edit Resource Type',
                            meta: {
                                label: 'Edit Resource Type',
                                resource: 'ResourceAdmin',
                            },
                            props: (route) => ({
                                formType: FORM_TYPE_EDIT,
                                resourceTypeId: route.params.id,
                            }),
                            beforeEnter: authService.requireAuth,
                            component: UnipharmAdminResourceTypeForm,
                        },
                    ]
                },
                {
                    path: '/helpAndResources',
                    name: 'Resources',
                    meta: {
                        label: process.env.VUE_APP_DEFAULT_RESOURCE_GROUP_LABEL || 'Pharmacy Resources',
                    },
                    beforeEnter: authService.requireAuth,
                    component: {
                        render() {
                            return h(resolveComponent('router-view'));
                        }
                    },
                    children: [
                        {
                            // :resourceType is the blobFolder slug of the resource type
                            // e.g. /helpAndResources/unipharmPharmacyNews
                            path: ':resourceType',
                            name: 'Resource',
                            meta: {
                                label: (route) => route.params.resourceType,
                            },
                            props: (route) => ({
                                resourceType: route.params.resourceType,
                            }),
                            beforeEnter: authService.requireAuth,
                            component: () => import('@/views/resources/HelpAndResources'),
                        },
                    ],
                },
                {
                    // Standalone resource types live at /<blobFolder> (top-level).
                    // This catch-all must come last among Home children so it does
                    // not shadow any of the specific named routes above.
                    path: '/:resourceType',
                    name: 'Standalone Resource',
                    meta: {
                        label: 'Resource',
                    },
                    props: (route) => ({
                        resourceType: route.params.resourceType,
                    }),
                    beforeEnter: authService.requireAuth,
                    component: () => import('@/views/resources/HelpAndResources'),
                },
                // TODO: To be implemented post go live
                /*
                {
                    path: '/reports',
                    redirect: '/reports/monthlyPurchases',
                    name: 'Reports',
                    meta: {
                        label: 'Reports',
                    },
                    beforeEnter: authService.requireAuth,
                    component: {
                        render() {
                            return h(resolveComponent('router-view'));
                        }
                    },
                    children: [ 
                        {
                            path: '/reports/monthlyPurchases',
                            name: 'Monthly Purchases',
                            meta: {
                                label: 'Monthly Purchases',
                                resource: 'SeePurchaseReports',
                            },
                            beforeEnter: authService.requireAuth,
                            component: MonthlyPurchasesReport,
                            children: [
                                {
                                    path: 'accountGroup/:accountGroup/:month/:year',
                                    name: 'Monthly Purchases by Account Group',
                                    meta: {
                                        label: 'Monthly Purchases by Account Group',
                                        resource: 'SeePurchaseReports',
                                    },
                                    props: (route) => ({
                                        accountGroup: route.params.accountGroup,
                                        month: route.params.month,
                                        year: route.params.year,
                                    }),
                                    beforeEnter: authService.requireAuth,
                                    component: MonthlyPurchasesAccountGroupReport,
                                    children: [
                                        {
                                            path: 'itemGroup/:itemGroup',
                                            name: 'Monthly Purchases by Item Group',
                                            meta: {
                                                label: 'Monthly Purchases by Item Group',
                                                resource: 'SeePurchaseReports',
                                            },
                                            props: (route) => ({
                                                itemGroup: route.params.itemGroup
                                            }),
                                            beforeEnter: authService.requireAuth,
                                            component: MonthlyPurchasesItemGroupReport
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                },
                */
            ],
        },
        {
            path: '/pages',
            redirect: '/pages/404',
            name: 'Pages',
            component: {
                render() {
                    return h(resolveComponent('router-view'));
                }
            },
            children: [
                {
                    path: '404',
                    name: 'Page404',
                    component: Page404
                },
                {
                    path: '500',
                    name: 'Page500',
                    component: Page500
                },
                {
                    path: 'notauth',
                    name: 'NotAuth',
                    component: NotAuth
                },
                {
                    path: 'login',
                    name: 'Login',
                    component: Login
                },
                {
                    path: 'forgotPassword',
                    name: 'ForgotPassword',
                    component: ForgotPassword
                },
                {
                    path: 'resetPassword',
                    name: 'ResetPassword',
                    component: ResetPassword
                },
                {
                    path: 'emailPrompt',
                    name: 'Email Prompt',
                    component: EmailPrompt,
                    beforeEnter: authService.isUserLoggedIn,
                },
                {
                    path: 'legacyPasswordPrompt',
                    name: 'Legacy Password Prompt',
                    component: LegacyPasswordPrompt,
                    beforeEnter: authService.isUserLoggedIn,
                },                
            ]
        }
    ];
}
